"use client";

import Link from "next/link";
import { useState } from "react";
import { ChevronDown, Ruler, Settings, Layers, HelpCircle, Sparkles, Home, ArrowRight } from "lucide-react";

interface FAQItem {
  question: string;
  answer: string;
  link?: string;
  linkText?: string;
}

interface FAQCategory {
  title: string;
  icon: React.ReactNode;
  faqs: FAQItem[];
}

export default function GuidesPage() {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const toggleItem = (id: string) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const faqCategories: FAQCategory[] = [
    {
      title: "Measuring",
      icon: <Ruler className="w-6 h-6" />,
      faqs: [
        {
          question: "How do I measure my windows for blinds?",
          answer: "For inside mount, measure the width at the top, middle, and bottom of the window opening. Use the smallest width measurement. For height, measure from the top of the opening to the windowsill at left, center, and right. Use the longest height measurement. Always measure to the nearest 1/8 inch.",
          link: "/guides/measuring",
          linkText: "View Complete Measuring Guide"
        },
        {
          question: "What's the difference between inside and outside mount?",
          answer: "Inside mount fits inside the window frame for a clean, built-in look. It requires at least 1\" depth. Outside mount attaches to the wall or frame outside the window opening - ideal for windows without enough depth or when you want to make windows appear larger.",
          link: "/guides/mounting",
          linkText: "Learn More About Mount Types"
        },
        {
          question: "How do I measure bay windows?",
          answer: "Measure each window section of the bay separately. For inside mount, measure width and height of each panel. For outside mount, measure from the outer edges. Consider the angles between panels - typically 30°, 45°, or 90°. We recommend individual blinds for each section rather than one continuous treatment.",
          link: "/guides/measuring-bay-windows",
          linkText: "Bay Window Measuring Guide"
        },
        {
          question: "How do I measure for sliding glass doors?",
          answer: "For vertical blinds on sliding doors, measure the width from outside edge to outside edge, and height from the top of the opening to the floor. Add 3-4 inches to width for proper light blockage. For the stack direction, choose the side opposite to where the door opens.",
          link: "/guides/measuring-sliding-doors",
          linkText: "Sliding Door Measuring Guide"
        },
        {
          question: "How do I measure arched or specialty shaped windows?",
          answer: "For arched windows, measure the width at the widest point (base of arch) and the height from the base to the highest point of the arch. For perfect arches, the height of the arch equals half the width. Custom templates may be needed for unusual shapes.",
          link: "/guides/measuring-arched-windows",
          linkText: "Arched Window Guide"
        },
        {
          question: "How do I measure skylights?",
          answer: "Measure the exact width and length of the skylight opening. Note the depth available for mounting hardware. Consider whether you need a fixed shade or operable one with remote control for hard-to-reach skylights.",
          link: "/guides/measuring-skylights",
          linkText: "Skylight Measuring Guide"
        }
      ]
    },
    {
      title: "Installation",
      icon: <Settings className="w-6 h-6" />,
      faqs: [
        {
          question: "Can I install blinds myself?",
          answer: "Yes! Most blinds come with easy-to-follow instructions and can be installed with basic tools (drill, screwdriver, level, pencil). Inside mount is generally easier than outside mount. Allow 15-30 minutes per window depending on your experience level.",
          link: "/guides/installation",
          linkText: "View Installation Guide"
        },
        {
          question: "What tools do I need for installation?",
          answer: "Basic tools include: a drill with bits, Phillips screwdriver, measuring tape, pencil, level, and step ladder. For metal or tile surfaces, you may need special anchors. Most hardware is included with your blinds."
        },
        {
          question: "How do I install blinds on different surfaces?",
          answer: "For drywall, use the provided wall anchors. For wood frames, screws can go directly into the wood. For plaster, drill pilot holes first. For concrete or brick, use masonry anchors. For metal frames, you may need self-tapping screws."
        },
        {
          question: "Do you offer professional installation?",
          answer: "Yes! We offer professional measuring and installation services. Our certified installers ensure perfect fit and proper operation. This is especially recommended for motorized blinds, large windows, or specialty shapes.",
          link: "/measure-install",
          linkText: "Book Professional Installation"
        }
      ]
    },
    {
      title: "Choosing the Right Product",
      icon: <HelpCircle className="w-6 h-6" />,
      faqs: [
        {
          question: "What's the best blind for light control?",
          answer: "For maximum light control, consider: Blackout roller shades (99% light blocking), cellular shades with blackout fabric, or wood/faux wood blinds with close-fitting slats. Room darkening options block 95-99% of light, while light filtering allows soft diffused light.",
          link: "/guides/choosing-blinds",
          linkText: "Choosing Guide"
        },
        {
          question: "What blinds are best for high humidity areas like bathrooms?",
          answer: "Polyester roller, solar and cellular fabrics shrug off steam, and a top-down bottom-up cellular shade gives privacy from the street while letting light in from above. Avoid real wood in bathrooms or kitchens with high humidity.",
          link: "/bathroom-blinds",
          linkText: "Bathroom Blinds Guide"
        },
        {
          question: "What are cellular shades and why should I consider them?",
          answer: "Cellular (honeycomb) shades have a unique cellular structure that traps air, providing excellent insulation - up to 50% energy savings. They come in single, double, or triple cell options and various opacity levels from sheer to blackout.",
          link: "/guides/cellular-shades",
          linkText: "Cellular Shades Guide"
        },
        {
          question: "Are motorized blinds worth it?",
          answer: "Motorized blinds offer convenience (remote/app control), safety (no cords for children/pets), energy efficiency (scheduled operation), and are essential for hard-to-reach windows. They integrate with smart home systems like Alexa, Google Home, and HomeKit.",
          link: "/guides/motorized-blinds",
          linkText: "Motorized Blinds Guide"
        }
      ]
    },
    {
      title: "Product Types",
      icon: <Layers className="w-6 h-6" />,
      faqs: [
        {
          question: "What are zebra blinds?",
          answer: "Zebra blinds (also called dual or layered shades) are a roller shade whose fabric alternates sheer and solid bands. Roll them so the solid bands line up for privacy, or offset them so light comes through the sheer bands — a view and privacy from one shade.",
          link: "/zebra-blinds",
          linkText: "Zebra Blinds Guide"
        },
        {
          question: "What are roller shades?",
          answer: "Roller shades are a simple, modern window covering that rolls up onto a tube at the top of the window. They're available in solar, light filtering, room darkening, and blackout fabrics. Popular for their clean lines and contemporary look.",
          link: "/guides/roller-shades",
          linkText: "Roller Shades Guide"
        },
        {
          question: "What are solar shades?",
          answer: "Solar shades are roller shades in a woven screen fabric that cuts glare and UV while keeping your view. The weave's openness (1%, 3% or 5%) sets how much light and view come through — lower openness blocks more sun, higher openness keeps more view.",
          link: "/solar-shade-openness-1-vs-3-vs-5",
          linkText: "Solar Shade Openness Guide"
        }
      ]
    },
    {
      title: "Care & Maintenance",
      icon: <Sparkles className="w-6 h-6" />,
      faqs: [
        {
          question: "How do I clean my blinds?",
          answer: "For regular cleaning, dust with a microfiber cloth or vacuum with brush attachment. For deeper cleaning: wood/faux wood blinds - damp cloth, never soak; fabric shades - spot clean or professional cleaning; aluminum/vinyl - mild soap and water. Clean slats in the closed position.",
          link: "/guides/care-maintenance",
          linkText: "Complete Care Guide"
        },
        {
          question: "How often should I clean my blinds?",
          answer: "Dust weekly with a duster or microfiber cloth. Deep clean every 3-6 months depending on environment. Kitchen blinds and those in dusty areas may need more frequent cleaning."
        },
        {
          question: "My blind cord is tangled. How do I fix it?",
          answer: "First, lower the blind completely. Gently separate tangled cords. Pull each cord individually to ensure smooth operation. If cords are severely tangled or damaged, contact our customer service for cord replacement options."
        },
        {
          question: "How do I fix a stuck or uneven blind?",
          answer: "For stuck blinds, check for obstructions in the track or cord lock mechanism. For uneven blinds, remove from brackets and realign the lift cords. Ensure the headrail is level. If the problem persists, the internal mechanism may need repair."
        }
      ]
    }
  ];

  return (
    <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8">
      {/* Hero Section */}
      <div className="bg-charcoal rounded-lg p-5 sm:p-8 md:p-10 mb-6 sm:mb-10 shadow-lg">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3 text-white leading-tight">
            Frequently Asked Questions
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-white/90 leading-relaxed">
            Find answers to common questions about measuring, installing, choosing, and caring for your window treatments.
          </p>
        </div>
      </div>

      {/* Quick Links */}
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6 sm:mb-10">
        {faqCategories.map((category) => (
          <a
            key={category.title}
            href={`#${category.title.toLowerCase().replace(/\s+/g, '-')}`}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white border border-gray-200 rounded-full text-xs sm:text-sm font-medium text-gray-700 hover:bg-sb-bg hover:border-charcoal hover:text-sb-gold transition-all shadow-sm"
          >
            <span className="w-4 h-4 sm:w-5 sm:h-5 [&>svg]:w-full [&>svg]:h-full">{category.icon}</span>
            <span className="hidden sm:inline">{category.title}</span>
            <span className="sm:hidden">{category.title.split(' ')[0]}</span>
          </a>
        ))}
      </div>

      {/* FAQ Sections */}
      <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8 mb-8 sm:mb-12">
        {faqCategories.map((category) => (
          <div key={category.title} id={category.title.toLowerCase().replace(/\s+/g, '-')}>
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-sb-alt rounded-lg flex items-center justify-center text-sb-gold [&>svg]:w-4 [&>svg]:h-4 sm:[&>svg]:w-5 sm:[&>svg]:h-5">
                {category.icon}
              </div>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-heading-charcoal">{category.title}</h2>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden divide-y divide-gray-200">
              {category.faqs.map((faq, index) => {
                const itemId = `${category.title}-${index}`;
                const isOpen = openItems[itemId];

                return (
                  <div key={index} className="group">
                    <button
                      onClick={() => toggleItem(itemId)}
                      className="w-full flex items-center justify-between px-3 sm:px-5 py-3 sm:py-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-gray-900 pr-2 sm:pr-4 text-sm sm:text-base">{faq.question}</span>
                      <ChevronDown
                        className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>

                    <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-[500px]' : 'max-h-0'}`}>
                      <div className="px-3 sm:px-5 pb-3 sm:pb-4 text-gray-600">
                        <p className="mb-2 sm:mb-3 text-sm sm:text-base">{faq.answer}</p>
                        {faq.link && (
                          <Link
                            href={faq.link}
                            className="inline-flex items-center gap-1 text-sb-gold font-medium hover:underline text-sm"
                          >
                            {faq.linkText || "Learn More"}
                            <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Detailed Guides Section */}
      <div className="max-w-4xl mx-auto mb-8 sm:mb-12">
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-heading-charcoal mb-4 sm:mb-6 text-center">Detailed Guides</h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
          {[
            { title: "Measuring Guide", href: "/guides/measuring", icon: <Ruler className="w-4 h-4 sm:w-5 sm:h-5" /> },
            { title: "Installation Guide", href: "/guides/installation", icon: <Settings className="w-4 h-4 sm:w-5 sm:h-5" /> },
            { title: "Mount Types", href: "/guides/mounting", icon: <Layers className="w-4 h-4 sm:w-5 sm:h-5" /> },
            { title: "Choosing Blinds", href: "/guides/choosing-blinds", icon: <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5" /> },
            { title: "Care & Maintenance", href: "/guides/care-maintenance", icon: <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" /> },
            { title: "Cellular Shades", href: "/guides/cellular-shades", icon: <Layers className="w-4 h-4 sm:w-5 sm:h-5" /> },
            { title: "Roller Shades", href: "/guides/roller-shades", icon: <Layers className="w-4 h-4 sm:w-5 sm:h-5" /> },
            { title: "Motorized Blinds", href: "/guides/motorized-blinds", icon: <Settings className="w-4 h-4 sm:w-5 sm:h-5" /> },
            { title: "Zebra Blinds", href: "/zebra-blinds", icon: <Layers className="w-4 h-4 sm:w-5 sm:h-5" /> },
            { title: "Solar Shades", href: "/solar-shades", icon: <Home className="w-4 h-4 sm:w-5 sm:h-5" /> },
          ].map((guide) => (
            <Link
              key={guide.href}
              href={guide.href}
              className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-lg hover:border-charcoal hover:shadow-md transition-all group"
            >
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-sb-alt rounded-lg flex items-center justify-center text-sb-gold group-hover:bg-charcoal group-hover:text-white transition-colors flex-shrink-0">
                {guide.icon}
              </div>
              <span className="font-medium text-gray-700 group-hover:text-sb-gold transition-colors text-xs sm:text-base truncate">{guide.title}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Professional Services CTA */}
      <div className="bg-sb rounded-lg p-5 sm:p-8 md:p-10 text-center shadow-md border border-gray-200 max-w-4xl mx-auto">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3 text-heading-charcoal">Still Have Questions?</h2>
        <p className="text-sm sm:text-base md:text-lg text-sb-text-muted mb-4 sm:mb-6 max-w-2xl mx-auto leading-relaxed">
          Our expert team is here to help! Book a free consultation or contact us directly.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <Link
            href="/consultation"
            className="bg-primary-red hover:bg-primary-dark text-white font-medium py-2.5 sm:py-3 px-5 sm:px-6 rounded-lg transition-colors inline-block text-sm sm:text-base"
          >
            Free Consultation
          </Link>
          <Link
            href="/measure-install"
            className="bg-white hover:bg-sb-bg text-heading-charcoal font-medium py-2.5 sm:py-3 px-5 sm:px-6 rounded-lg border border-gray-300 transition-colors inline-block text-sm sm:text-base"
          >
            Professional Services
          </Link>
        </div>
      </div>
    </div>
  );
}
